import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Wrench, Cpu } from "lucide-react";

interface ToolDefinition {
  id: string;
  name: string;
  tool_type: string;
  requires_serial_number: boolean;
  requires_inventory_number: boolean;
  default_replacement_cost: number;
  sort_order: number;
  is_standard_copper: boolean;
  is_standard_fiber: boolean;
}

export function ToolDefinitionsSettings() {
  const { user } = useAuth();
  const [tools, setTools] = useState<ToolDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    tool_type: "hand_tool",
    requires_serial_number: false,
    requires_inventory_number: false,
    default_replacement_cost: 0,
    is_standard_copper: false,
    is_standard_fiber: false,
  });

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      const { data, error } = await supabase
        .from("tool_definitions")
        .select("*")
        .order("sort_order");

      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      console.error("Error loading tools:", error);
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      name: "",
      tool_type: "hand_tool",
      requires_serial_number: false,
      requires_inventory_number: false,
      default_replacement_cost: 0,
      is_standard_copper: false,
      is_standard_fiber: false,
    });
    setDialogOpen(true);
  };

  const handleEdit = (tool: ToolDefinition) => {
    setEditingId(tool.id);
    setFormData({
      name: tool.name,
      tool_type: tool.tool_type,
      requires_serial_number: tool.requires_serial_number,
      requires_inventory_number: tool.requires_inventory_number,
      default_replacement_cost: tool.default_replacement_cost,
      is_standard_copper: tool.is_standard_copper,
      is_standard_fiber: tool.is_standard_fiber,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Bitte Namen eingeben");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("tool_definitions")
          .update({
            name: formData.name,
            tool_type: formData.tool_type,
            requires_serial_number: formData.requires_serial_number,
            requires_inventory_number: formData.requires_inventory_number,
            default_replacement_cost: formData.default_replacement_cost,
            is_standard_copper: formData.is_standard_copper,
            is_standard_fiber: formData.is_standard_fiber,
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Werkzeug aktualisiert");
      } else {
        const maxOrder = Math.max(...tools.map((t) => t.sort_order), 0);
        const { error } = await supabase.from("tool_definitions").insert([
          {
            name: formData.name,
            tool_type: formData.tool_type,
            requires_serial_number: formData.requires_serial_number,
            requires_inventory_number: formData.requires_inventory_number,
            default_replacement_cost: formData.default_replacement_cost,
            is_standard_copper: formData.is_standard_copper,
            is_standard_fiber: formData.is_standard_fiber,
            sort_order: maxOrder + 1,
          },
        ]);

        if (error) throw error;
        toast.success("Werkzeug hinzugefügt");
      }

      setDialogOpen(false);
      loadTools();
    } catch (error) {
      console.error("Error saving tool:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie dieses Werkzeug wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("tool_definitions").delete().eq("id", id);

      if (error) throw error;
      toast.success("Werkzeug gelöscht");
      loadTools();
    } catch (error) {
      console.error("Error deleting tool:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const handTools = tools.filter((t) => t.tool_type === "hand_tool");
  const machines = tools.filter((t) => t.tool_type === "machine");

  const renderTable = (items: ToolDefinition[], title: string, icon: React.ReactNode) => (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">{icon}</div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Standardkosten</TableHead>
              <TableHead>S/N</TableHead>
              <TableHead>Inv.-Nr.</TableHead>
              <TableHead>Standard Kupfer</TableHead>
              <TableHead>Standard Glasfaser</TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((tool) => (
              <TableRow key={tool.id}>
                <TableCell className="font-medium">{tool.name}</TableCell>
                <TableCell>
                  {tool.default_replacement_cost.toLocaleString("de-DE", {
                    style: "currency",
                    currency: "EUR",
                  })}
                </TableCell>
                <TableCell>
                  {tool.requires_serial_number ? (
                    <Badge>Ja</Badge>
                  ) : (
                    <Badge variant="secondary">Nein</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {tool.requires_inventory_number ? (
                    <Badge>Ja</Badge>
                  ) : (
                    <Badge variant="secondary">Nein</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {tool.is_standard_copper ? (
                    <Badge className="bg-orange-500">Ja</Badge>
                  ) : (
                    <Badge variant="secondary">Nein</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {tool.is_standard_fiber ? (
                    <Badge className="bg-blue-500">Ja</Badge>
                  ) : (
                    <Badge variant="secondary">Nein</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(tool)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(tool.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Neues Werkzeug/Gerät
        </Button>
      </div>

      {renderTable(handTools, "Handwerkzeuge", <Wrench className="h-5 w-5 text-primary" />)}
      {renderTable(machines, "Maschinen & Messgeräte", <Cpu className="h-5 w-5 text-primary" />)}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Werkzeug bearbeiten" : "Neues Werkzeug/Gerät"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Akkuschrauber Bosch"
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select
                value={formData.tool_type}
                onValueChange={(value) => setFormData({ ...formData, tool_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hand_tool">Handwerkzeug</SelectItem>
                  <SelectItem value="machine">Maschine/Messgerät</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Standardkosten (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.default_replacement_cost}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    default_replacement_cost: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Seriennummer erforderlich</Label>
              <Switch
                checked={formData.requires_serial_number}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_serial_number: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Inventarnummer erforderlich</Label>
              <Switch
                checked={formData.requires_inventory_number}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_inventory_number: checked })
                }
              />
            </div>
            
            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-medium mb-3">Standardausstattung für:</p>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-500" />
                  Kupferinstallateure
                </Label>
                <Switch
                  checked={formData.is_standard_copper}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_standard_copper: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  Glasfasermonteure
                </Label>
                <Switch
                  checked={formData.is_standard_fiber}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_standard_fiber: checked })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
